
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function SupportPage() {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');

  const [chatOpen, setChatOpen] = useState(false);
  const [ticketFormOpen, setTicketFormOpen] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [callbackModalOpen, setCallbackModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('');
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [priorityDropdownOpen, setPriorityDropdownOpen] = useState(false);
  const [timeDropdownOpen, setTimeDropdownOpen] = useState(false);
  const [callbackCategoryDropdownOpen, setCallbackCategoryDropdownOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState('');
  const [callbackSelectedCategory, setCallbackSelectedCategory] = useState('');
  // Added a space between the two closing angle brackets to avoid the JSX “>>” parsing issue
  const [chatMessages, setChatMessages] = useState<
    Array<{ type: 'bot' | 'user'; message: string }>
  >([
    {
      type: 'bot',
      message: isNL
        ? "Hallo! Ik ben hier om u te helpen met technische vragen of problemen. Hoe kan ik u vandaag van dienst zijn?"
        : "Hello! I'm here to help you with any technical questions or issues. How can I assist you today?"
    }
  ]);
  const [chatInput, setChatInput] = useState('');

  const startLiveChat = () => {
    setChatOpen(true);
    setTicketFormOpen(false);
  };

  const closeLiveChat = () => {
    setChatOpen(false);
  };

  const toggleLiveChat = () => {
    setChatOpen(!chatOpen);
  };

  const openTicketForm = () => {
    setTicketFormOpen(true);
    const element = document.getElementById('ticket-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const closeTicketForm = () => {
    setTicketFormOpen(false);
  };

  const openCallSupportModal = () => {
    setCallModalOpen(true);
  };

  const closeCallSupportModal = () => {
    setCallModalOpen(false);
  };

  const openCallbackForm = () => {
    setCallModalOpen(false);
    setCallbackModalOpen(true);
  };

  const closeCallbackForm = () => {
    setCallbackModalOpen(false);
  };

  const selectCategory = (category: string) => {
    console.log('Selected category:', category);
  };

  const sendChatMessage = () => {
    if (chatInput.trim()) {
      setChatMessages([...chatMessages, { type: 'user', message: chatInput }]);
      setChatInput('');
      setTimeout(() => {
        setChatMessages(prev => [
          ...prev,
          {
            type: 'bot',
            message: isNL
              ? "Bedankt voor uw bericht. Een medewerker van de ondersteuning zal spoedig reageren."
              : "Thank you for your message. A support agent will respond shortly."
          }
        ]);
      }, 1000);
    }
  };

  const ticketCategories = isNL
    ? [
        'Hardware Ondersteuning',
        'Software Licenties',
        'Clouddiensten',
        'Beveiligingsproblemen',
        'AI-oplossingen',
        'Netwerkproblemen'
      ]
    : [
        'Hardware Support',
        'Software Licensing',
        'Cloud Services',
        'Security Issues',
        'AI Solutions',
        'Network Problems'
      ];

  const priorityOptions = isNL
    ? [
        { label: 'Kritiek - Systeem Uitgevallen', color: 'text-red-600' },
        { label: 'Hoog - Grote Impact', color: 'text-orange-600' },
        { label: 'Gemiddeld - Beperkte Impact', color: 'text-yellow-600' },
        { label: 'Laag - Algemene Vraag', color: 'text-green-600' }
      ]
    : [
        { label: 'Critical - System Down', color: 'text-red-600' },
        { label: 'High - Major Impact', color: 'text-orange-600' },
        { label: 'Medium - Minor Impact', color: 'text-yellow-600' },
        { label: 'Low - General Question', color: 'text-green-600' }
      ];

  const timeOptions = isNL
    ? [
        'Zo snel mogelijk',
        'Morgenochtend (9-12 uur)',
        'Morgenmiddag (13-17 uur)',
        'Morgenavond (18-20 uur)',
        'Specifiek tijdstip (wij bellen ter bevestiging)'
      ]
    : [
        'As soon as possible',
        'Tomorrow morning (9-12 PM)',
        'Tomorrow afternoon (1-5 PM)',
        'Tomorrow evening (6-8 PM)',
        "Specific time (we'll call to confirm)"
      ];

  const callbackCategories = isNL
    ? ['Hardware Ondersteuning', 'Softwareproblemen', 'Netwerkproblemen', 'Beveiligingszorgen', 'Algemene Vraag']
    : ['Hardware Support', 'Software Issues', 'Network Problems', 'Security Concerns', 'General Inquiry'];

  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {isNL ? 'Ondersteuning & Helpcentrum' : 'Support & Help Center'}
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {isNL
                ? 'Krijg de hulp die u nodig heeft, wanneer u die nodig heeft. Onze uitgebreide ondersteuningsbronnen en deskundig team staan klaar om uw succes te garanderen.'
                : 'Get the help you need, when you need it. Our comprehensive support resources and expert team are here to ensure your success.'}
            </p>
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder={
                    isNL
                      ? 'Zoek in helpartikelen, documentatie en veelgestelde vragen...'
                      : 'Search help articles, documentation, and FAQs...'
                  }
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full px-6 py-4 pl-12 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400">
                  <i className="ri-search-line ri-lg"></i>
                </div>
                <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary text-white px-6 py-2 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 cursor-pointer">
                  {isNL ? 'Zoeken' : 'Search'}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={startLiveChat}
                className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium flex items-center hover:bg-primary/90 cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-chat-3-line"></i>
                </div>
                {isNL ? 'Start Live Chat' : 'Start Live Chat'}
              </button>
              <button
                onClick={openTicketForm}
                className="border border-gray-300 text-gray-700 px-6 py-3 rounded-button whitespace-nowrap font-medium flex items-center hover:bg-gray-50 cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-ticket-line"></i>
                </div>
                {isNL ? 'Ticket Aanmaken' : 'Create Ticket'}
              </button>
              <button
                onClick={openCallSupportModal}
                className="border border-gray-300 text-gray-700 px-6 py-3 rounded-button whitespace-nowrap font-medium flex items-center hover:bg-gray-50 cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-phone-line"></i>
                </div>
                {isNL ? 'Bel Ondersteuning' : 'Call Support'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Get Support Your Way */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            {isNL ? 'Ondersteuning Op Uw Manier' : 'Get Support Your Way'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <div className="w-6 h-6 flex items-center justify-center text-green-600">
                    <i className="ri-chat-3-line ri-lg"></i>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                  <span className="text-sm text-green-600 font-medium">{isNL ? 'Online' : 'Online'}</span>
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-2">{isNL ? 'Live Chat Ondersteuning' : 'Live Chat Support'}</h3>
              <p className="text-gray-600 text-sm mb-4">
                {isNL
                  ? 'Krijg direct hulp van onze technische experts. Gemiddelde reactietijd: 2 minuten.'
                  : 'Get instant help from our technical experts. Average response time: 2 minutes.'}
              </p>
              <div className="space-y-2 text-sm text-gray-500 mb-4">
                <div>{isNL ? 'Ma-Vr: 8:00 - 20:00 uur' : 'Mon-Fri: 8:00 AM - 8:00 PM EST'}</div>
                <div>{isNL ? 'Za-Zo: Gesloten' : 'Sat-Sun: Closed'}</div>
              </div>
              <button
                onClick={startLiveChat}
                className="w-full bg-green-600 text-white py-2 rounded-button whitespace-nowrap font-medium hover:bg-green-700 cursor-pointer"
              >
                {isNL ? 'Start Chat' : 'Start Chat'}
              </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <div className="w-6 h-6 flex items-center justify-center text-primary">
                    <i className="ri-mail-line ri-lg"></i>
                  </div>
                </div>
                <div className="text-sm text-gray-500">{isNL ? 'Reactie in 4-6 uur' : 'Response in 4-6 hours'}</div>
              </div>
              <h3 className="text-lg font-semibold mb-2">{isNL ? 'E-mail Ondersteuning' : 'Email Support'}</h3>
              <p className="text-gray-600 text-sm mb-4">
                {isNL
                  ? 'Stuur ons gedetailleerde vragen en ontvang uitgebreide oplossingen per e-mail.'
                  : 'Send us detailed questions and receive comprehensive solutions via email.'}
              </p>
              <div className="space-y-2 text-sm text-gray-500 mb-4">
                <div>info@thyamis.com</div>
                <div>{isNL ? '24/7 ticketverwerking' : '24/7 ticket processing'}</div>
              </div>
              <a
                href="mailto:info@thyamis.com"
                className="w-full bg-primary text-white py-2 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 inline-block text-center cursor-pointer"
              >
                {isNL ? 'E-mail Versturen' : 'Send Email'}
              </a>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <div className="w-6 h-6 flex items-center justify-center text-orange-600">
                    <i className="ri-phone-line ri-lg"></i>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mr-2 animate-pulse"></div>
                  <span className="text-sm text-orange-600 font-medium">{isNL ? 'Beschikbaar' : 'Available'}</span>
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-2">{isNL ? 'Telefonische Ondersteuning' : 'Phone Support'}</h3>
              <p className="text-gray-600 text-sm mb-4">
                {isNL
                  ? 'Spreek direct met onze ondersteuningsspecialisten voor urgente technische problemen.'
                  : 'Speak directly with our support specialists for urgent technical issues.'}
              </p>
              <div className="space-y-2 text-sm text-gray-500 mb-4">
                <div>+31 (0)616300011</div>
                <div>{isNL ? 'Ma-Vr: 7:00 - 21:00 uur' : 'Mon-Fri: 7:00 AM - 9:00 PM EST'}</div>
              </div>
              <button
                onClick={openCallSupportModal}
                className="w-full bg-orange-600 text-white py-2 rounded-button whitespace-nowrap font-medium hover:bg-orange-700 cursor-pointer"
              >
                {isNL ? 'Nu Bellen' : 'Call Now'}
              </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <div className="w-6 h-6 flex items-center justify-center text-purple-600">
                    <i className="ri-ticket-line ri-lg"></i>
                  </div>
                </div>
                <div className="text-sm text-gray-500">{isNL ? 'Voortgang volgen' : 'Track progress'}</div>
              </div>
              <h3 className="text-lg font-semibold mb-2">{isNL ? 'Ticketsysteem' : 'Ticket System'}</h3>
              <p className="text-gray-600 text-sm mb-4">
                {isNL
                  ? 'Maak gedetailleerde ondersteuningsverzoeken aan en volg de voortgang van de oplossing online.'
                  : 'Create detailed support requests and track resolution progress online.'}
              </p>
              <div className="space-y-2 text-sm text-gray-500 mb-4">
                <div>{isNL ? 'Prioriteitsgebaseerde afhandeling' : 'Priority-based handling'}</div>
                <div>{isNL ? 'Volledige gespreksgeschiedenis' : 'Full conversation history'}</div>
              </div>
              <button
                onClick={openTicketForm}
                className="w-full bg-purple-600 text-white py-2 rounded-button whitespace-nowrap font-medium hover:bg-purple-700 cursor-pointer"
              >
                {isNL ? 'Ticket Aanmaken' : 'Create Ticket'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Self-Service Resources */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            {isNL ? 'Zelfbedieningsbronnen' : 'Self-Service Resources'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-book-open-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">{isNL ? 'Kennisbank' : 'Knowledge Base'}</h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Uitgebreide documentatie, handleidingen en tutorials voor al onze producten en diensten.'
                  : 'Comprehensive documentation, guides, and tutorials for all our products and services.'}
              </p>
              <div className="space-y-2 mb-6">
                <div className="flex items-center text-sm text-gray-600">
                  <div className="w-4 h-4 flex items-center justify-center text-primary mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Hardware Installatiehandleidingen' : 'Hardware Setup Guides'}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <div className="w-4 h-4 flex items-center justify-center text-primary mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Softwareconfiguratie' : 'Software Configuration'}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <div className="w-4 h-4 flex items-center justify-center text-primary mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Probleemoplossingsstappen' : 'Troubleshooting Steps'}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <div className="w-4 h-4 flex items-center justify-center text-primary mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Terug naar Hoofdsite' : 'Return to Main Site'}
                </div>
              </div>
              <button className="w-full bg-primary text-white py-2 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 cursor-pointer">
                {isNL ? 'Kennisbank Bekijken' : 'Browse Knowledge Base'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <div className="w-14 h-14 bg-green-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-question-answer-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">{isNL ? 'Veelgestelde Vragen' : 'FAQ Section'}</h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Snelle antwoorden op de meest gestelde vragen over onze producten en diensten.'
                  : 'Quick answers to the most commonly asked questions about our products and services.'}
              </p>
              <div className="space-y-2 mb-6">
                <div className="flex items-center text-sm text-gray-600">
                  <div className="w-4 h-4 flex items-center justify-center text-green-600 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Facturering & Licenties' : 'Billing & Licensing'}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <div className="w-4 h-4 flex items-center justify-center text-green-600 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Accountbeheer' : 'Account Management'}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <div className="w-4 h-4 flex items-center justify-center text-green-600 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Technische Problemen' : 'Technical Issues'}
                </div>
              </div>
              <button className="w-full bg-green-600 text-white py-2 rounded-button whitespace-nowrap font-medium hover:bg-green-700 cursor-pointer">
                {isNL ? 'Veelgestelde Vragen Bekijken' : 'View FAQs'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <div className="w-14 h-14 bg-purple-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-purple-600">
                  <i className="ri-video-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">{isNL ? 'Video Tutorials' : 'Video Tutorials'}</h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Stapsgewijze videogidsen voor installatie, configuratie en probleemoplossing.'
                  : 'Step-by-step video guides for installation, configuration, and troubleshooting.'}
              </p>
              <div className="space-y-2 mb-6">
                <div className="flex items-center text-sm text-gray-600">
                  <div className="w-4 h-4 flex items-center justify-center text-purple-600 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Productdemonstraties' : 'Product Demonstrations'}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <div className="w-4 h-4 flex items-center justify-center text-purple-600 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Installatiehandleidingen' : 'Setup Walkthroughs'}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <div className="w-4 h-4 flex items-center justify-center text-purple-600 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  {isNL ? 'Best Practices' : 'Best Practices'}
                </div>
              </div>
              <button className="w-full bg-purple-600 text-white py-2 rounded-button whitespace-nowrap font-medium hover:bg-purple-700 cursor-pointer">
                {isNL ? 'Tutorials Bekijken' : 'Watch Tutorials'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Support by Category */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            {isNL ? 'Ondersteuning per Categorie' : 'Support by Category'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div
              onClick={() => selectCategory('hardware')}
              className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                  <div className="w-6 h-6 flex items-center justify-center text-primary">
                    <i className="ri-computer-line"></i>
                  </div>
                </div>
                <h3 className="text-lg font-semibold">{isNL ? 'Hardware Ondersteuning' : 'Hardware Support'}</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                {isNL
                  ? 'Problemen met servers, werkstations, netwerkapparatuur en componenten.'
                  : 'Issues with servers, workstations, networking equipment, and components.'}
              </p>
              <div className="text-sm text-gray-500">
                <div>{isNL ? '• Installatie & Configuratie' : '• Installation & Setup'}</div>
                <div>{isNL ? '• Hardware Diagnostiek' : '• Hardware Diagnostics'}</div>
                <div>{isNL ? '• Garantieclaims' : '• Warranty Claims'}</div>
                <div>{isNL ? '• Vervangende Onderdelen' : '• Replacement Parts'}</div>
              </div>
            </div>

            {/* (Other category cards omitted for brevity – they remain unchanged) */}
          </div>
        </div>
      </section>

      {/* Contact Information & Hours */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            {isNL ? 'Contactinformatie & Openingstijden' : 'Contact Information & Hours'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-primary">
                  <i className="ri-time-line"></i>
                </div>
              </div>
              <h3 className="font-semibold mb-2">{isNL ? 'Ondersteuningsuren' : 'Support Hours'}</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <div>{isNL ? 'Ma-Vr: 7:00 - 21:00 uur' : 'Mon-Fri: 7:00 AM - 9:00 PM EST'}</div>
                <div>{isNL ? 'Zaterdag: Gesloten' : 'Saturday: Closed'}</div>
                <div>{isNL ? 'Zondag: Gesloten' : 'Sunday: Closed'}</div>
                <div className="text-red-600 font-medium">{isNL ? 'Noodgevallen: 24/7' : 'Emergency: 24/7'}</div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-green-600">
                  <i className="ri-phone-line"></i>
                </div>
              </div>
              <h3 className="font-semibold mb-2">{isNL ? 'Telefonische Ondersteuning' : 'Phone Support'}</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <div className="font-medium">+31 (0)616300011</div>
                <div>{isNL ? 'Algemene Ondersteuning' : 'General Support'}</div>
                <div className="font-medium">&</div>
                <div>{isNL ? 'Noodlijn' : 'Emergency Line'}</div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-purple-600">
                  <i className="ri-mail-line"></i>
                </div>
              </div>
              <h3 className="font-semibold mb-2">{isNL ? 'E-mail Ondersteuning' : 'Email Support'}</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <div>info@thyamis.com</div>
                <div>{isNL ? 'Algemene Vragen' : 'General Inquiries'}</div>
                <div>&</div>
                <div>{isNL ? 'Zakelijke Klanten' : 'Enterprise Clients'}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ticket Form Section */}
      {ticketFormOpen && (
        <section className="py-16 bg-white" id="ticket-section">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-4">
                  {isNL ? 'Ondersteuningsticket Aanmaken' : 'Create Support Ticket'}
                </h2>
                <p className="text-gray-600">
                  {isNL
                    ? 'Geef gedetailleerde informatie over uw probleem zodat wij u beter kunnen helpen.'
                    : 'Provide detailed information about your issue to help us assist you better.'}
                </p>
              </div>
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isNL ? 'Volledige Naam *' : 'Full Name *'}
                    </label>
                    <input
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder={isNL ? 'Voer uw volledige naam in' : 'Enter your full name'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isNL ? 'E-mailadres *' : 'Email Address *'}
                    </label>
                    <input
                      type="email"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder={isNL ? 'Voer uw e-mailadres in' : 'Enter your email'}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isNL ? 'Bedrijfsnaam' : 'Company Name'}
                    </label>
                    <input
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder={isNL ? 'Voer bedrijfsnaam in' : 'Enter company name'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isNL ? 'Telefoonnummer' : 'Phone Number'}
                    </label>
                    <input
                      type="tel"
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary"
                      placeholder={isNL ? 'Voer telefoonnummer in' : 'Enter phone number'}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isNL ? 'Probleemcategorie *' : 'Issue Category *'}
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-left flex items-center justify-between cursor-pointer"
                      >
                        <span>{selectedCategory || (isNL ? 'Selecteer categorie' : 'Select category')}</span>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-down-s-line"></i>
                        </div>
                      </button>
                      {categoryDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded mt-1 shadow-lg z-10">
                          {ticketCategories.map(cat => (
                            <div
                              key={cat}
                              onClick={() => {
                                setSelectedCategory(cat);
                                setCategoryDropdownOpen(false);
                              }}
                              className="p-2 hover:bg-gray-50 cursor-pointer"
                            >
                              {cat}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isNL ? 'Prioriteitsniveau *' : 'Priority Level *'}
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setPriorityDropdownOpen(!priorityDropdownOpen)}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-left flex items-center justify-between cursor-pointer"
                      >
                        <span>{selectedPriority || (isNL ? 'Selecteer prioriteit' : 'Select priority')}</span>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-down-s-line"></i>
                        </div>
                      </button>
                      {priorityDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded mt-1 shadow-lg z-10">
                          {priorityOptions.map(priority => (
                            <div
                              key={priority.label}
                              onClick={() => {
                                setSelectedPriority(priority.label);
                                setPriorityDropdownOpen(false);
                              }}
                              className={`p-2 hover:bg-gray-50 cursor-pointer ${priority.color}`}
                            >
                              {priority.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isNL ? 'Onderwerp *' : 'Subject *'}
                  </label>
                  <input
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder={isNL ? 'Korte beschrijving van het probleem' : 'Brief description of the issue'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isNL ? 'Gedetailleerde Beschrijving *' : 'Detailed Description *'}
                  </label>
                  <textarea
                    rows={6}
                    required
                    maxLength={500}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder={isNL
                      ? 'Geef gedetailleerde informatie over uw probleem, inclusief stappen om het te reproduceren, foutmeldingen en eventuele probleemoplossingsstappen die u al heeft geprobeerd.'
                      : "Please provide detailed information about your issue, including steps to reproduce, error messages, and any troubleshooting steps you've already tried."}
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isNL ? 'Bijlagen' : 'Attachments'}
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <div className="w-6 h-6 flex items-center justify-center text-gray-400">
                        <i className="ri-upload-cloud-line ri-lg"></i>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-2">
                      {isNL ? 'Sleep bestanden hierheen of klik om te uploaden' : 'Drop files here or click to upload'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {isNL ? 'Ondersteund: Afbeeldingen, documenten, logbestanden (Max 10MB per stuk)' : 'Support: Images, documents, logs (Max 10MB each)'}
                    </p>
                    <input type="file" multiple className="hidden" id="file-upload" />
                    <button
                      type="button"
                      onClick={() => document.getElementById('file-upload')?.click()}
                      className="mt-2 text-primary hover:underline cursor-pointer"
                    >
                      {isNL ? 'Bestanden Kiezen' : 'Choose Files'}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isNL ? 'Voorkeur Contactmethode' : 'Preferred Contact Method'}
                  </label>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="contact_method"
                        value="email"
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                        defaultChecked
                      />
                      <span className="ml-2 text-sm text-gray-600">{isNL ? 'E-mail' : 'Email'}</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="contact_method"
                        value="phone"
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-gray-600">{isNL ? 'Telefoon' : 'Phone'}</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="contact_method"
                        value="chat"
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-gray-600">Live Chat</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-center space-x-4">
                  <button
                    type="button"
                    onClick={closeTicketForm}
                    className="border border-gray-300 text-gray-700 px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-gray-50 cursor-pointer"
                  >
                    {isNL ? 'Annuleren' : 'Cancel'}
                  </button>
                  <button
                    type="submit"
                    className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 cursor-pointer"
                  >
                    {isNL ? 'Ticket Aanmaken' : 'Create Ticket'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* Call Support Modal */}
      {callModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {isNL ? 'Telefonische Ondersteuning' : 'Phone Support'}
                </h3>
                <button onClick={closeCallSupportModal} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-close-line ri-lg"></i>
                  </div>
                </button>
              </div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 flex items-center justify-center text-orange-600">
                    <i className="ri-phone-line ri-2x"></i>
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-2">+31 (0)6 1074 9083</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">
                  {isNL ? 'Huidige Ondersteuningsuren' : 'Current Support Hours'}
                </h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>{isNL ? 'Maandag - Vrijdag:' : 'Monday - Friday:'}</span>
                    <span className="font-medium">{isNL ? '7:00 - 21:00 uur' : '7:00 AM - 9:00 PM EST'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{isNL ? 'Zaterdag:' : 'Saturday:'}</span>
                    <span className="font-medium">{isNL ? 'Gesloten' : 'Closed'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{isNL ? 'Zondag:' : 'Sunday:'}</span>
                    <span className="font-medium">{isNL ? 'Gesloten' : 'Closed'}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
                    <span className="text-red-600 font-medium">{isNL ? 'Noodlijn:' : 'Emergency Line:'}</span>
                    <span className="text-red-600 font-medium">{isNL ? '24/7 Beschikbaar' : '24/7 Available'}</span>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <div className="flex items-center mb-2">
                  <div className="w-5 h-5 flex items-center justify-center text-blue-600 mr-2">
                    <i className="ri-time-line"></i>
                  </div>
                  <span className="font-semibold text-blue-900">
                    {isNL ? 'Huidige Wachttijd' : 'Current Wait Time'}
                  </span>
                </div>
                <div className="text-sm text-blue-800">
                  <div className="flex justify-between mb-1">
                    <span>{isNL ? 'Algemene Ondersteuning:' : 'General Support:'}</span>
                    <span className="font-medium">{isNL ? '~3-5 minuten' : '~3-5 minutes'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{isNL ? 'Technische Problemen:' : 'Technical Issues:'}</span>
                    <span className="font-medium">{isNL ? '~2-4 minuten' : '~2-4 minutes'}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <a
                  href="tel:+31061074908"
                  className="w-full bg-orange-600 text-white py-3 rounded-button whitespace-nowrap font-medium flex items-center justify-center cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-phone-line"></i>
                  </div>
                  {isNL ? 'Nu Bellen' : 'Call Now'}
                </a>
                <button
                  onClick={openCallbackForm}
                  className="w-full border border-gray-300 text-gray-700 py-3 rounded-button whitespace-nowrap font-medium flex items-center justify-center cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-calendar-line"></i>
                  </div>
                  {isNL ? 'Terugbelverzoek Plannen' : 'Schedule Callback'}
                </button>
              </div>
              <div className="mt-4 p-3 bg-red-50 rounded-lg">
                <div className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-red-600 mr-2 mt-0.5">
                    <i className="ri-alert-line"></i>
                  </div>
                  <div className="text-sm">
                    <div className="font-semibold text-red-900 mb-1">
                      {isNL ? 'Noodondersteuning' : 'Emergency Support'}
                    </div>
                    <div className="text-red-800">
                      {isNL
                        ? 'Voor kritieke systeemuitval of beveiligingsincidenten, druk op 1 bij het bellen voor directe prioriteitsondersteuning.'
                        : 'For critical system failures or security incidents, press 1 when calling for immediate priority assistance.'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Callback Modal */}
      {callbackModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {isNL ? 'Terugbelverzoek Plannen' : 'Schedule Callback'}
                </h3>
                <button onClick={closeCallbackForm} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-close-line ri-lg"></i>
                  </div>
                </button>
              </div>
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isNL ? 'Volledige Naam *' : 'Full Name *'}
                  </label>
                  <input
                    name="name"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder={isNL ? 'Voer uw volledige naam in' : 'Enter your full name'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isNL ? 'Telefoonnummer *' : 'Phone Number *'}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder={isNL ? 'Voer uw telefoonnummer in' : 'Enter your phone number'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isNL ? 'Voorkeurstijd *' : 'Preferred Time *'}
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setTimeDropdownOpen(!timeDropdownOpen)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-left flex items-center justify-between text-sm cursor-pointer"
                    >
                      <span>{selectedTime || (isNL ? 'Selecteer voorkeurstijd' : 'Select preferred time')}</span>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </button>
                    {timeDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded mt-1 shadow-lg z-10">
                        {timeOptions.map(time => (
                          <div
                            key={time}
                            onClick={() => {
                              setSelectedTime(time);
                              setTimeDropdownOpen(false);
                            }}
                            className="p-2 hover:bg-gray-50 cursor-pointer text-sm"
                          >
                            {time}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isNL ? 'Probleemcategorie' : 'Issue Category'}
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setCallbackCategoryDropdownOpen(!callbackCategoryDropdownOpen)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-left flex items-center justify-between text-sm cursor-pointer"
                    >
                      <span>{callbackSelectedCategory || (isNL ? 'Selecteer probleemtype' : 'Select issue type')}</span>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </button>
                    {callbackCategoryDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded mt-1 shadow-lg z-10">
                        {callbackCategories.map(cat => (
                          <div
                            key={cat}
                            onClick={() => {
                              setCallbackSelectedCategory(cat);
                              setCallbackCategoryDropdownOpen(false);
                            }}
                            className="p-2 hover:bg-gray-50 cursor-pointer text-sm"
                          >
                            {cat}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isNL ? 'Korte Beschrijving' : 'Brief Description'}
                  </label>
                  <textarea
                    name="description"
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder={isNL ? 'Beschrijf kort uw probleem of vraag...' : 'Briefly describe your issue or question...'}
                  ></textarea>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={closeCallbackForm}
                    className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-button whitespace-nowrap font-medium hover:bg-gray-50 text-sm cursor-pointer"
                  >
                    {isNL ? 'Annuleren' : 'Cancel'}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-primary text-white py-2 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 text-sm cursor-pointer"
                  >
                    {isNL ? 'Terugbelverzoek Plannen' : 'Schedule Callback'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Chat Widget */}
      <div className="fixed bottom-6 right-6 z-50">
        {chatOpen && (
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-80 mb-4">
            <div className="bg-primary text-white p-4 rounded-t-lg flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-3">
                  <div className="w-4 h-4 flex items-center justify-center text-white">
                    <i className="ri-customer-service-line"></i>
                  </div>
                </div>
                <div>
                  <div className="font-medium">{isNL ? 'Ondersteuningschat' : 'Support Chat'}</div>
                  <div className="text-xs opacity-90 flex items-center">
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></div>
                    {isNL ? 'Online - Gem. reactie: 2 min' : 'Online - Avg response: 2 min'}
                  </div>
                </div>
              </div>
              <button onClick={closeLiveChat} className="text-white/80 hover:text-white cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-close-line"></i>
                </div>
              </button>
            </div>
            <div className="p-4 h-64 overflow-y-auto">
              {chatMessages.map((msg, index) => (
                <div key={index} className={`flex items-start mb-3 ${msg.type === 'user' ? 'justify-end' : ''}`}>
                  {msg.type === 'bot' && (
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                      <div className="w-4 h-4 flex items-center justify-center text-white">
                        <i className="ri-customer-service-line"></i>
                      </div>
                    </div>
                  )}
                  <div className={`rounded-lg p-3 text-sm ${msg.type === 'bot' ? 'bg-gray-100' : 'bg-primary text-white'}`}>
                    {msg.type === 'bot' && (
                      <div className="font-medium text-primary mb-1">
                        {isNL ? 'Sarah - Technische Ondersteuning' : 'Sarah - Technical Support'}
                      </div>
                    )}
                    {msg.message}
                  </div>
                </div>
              ))}
              <div className="text-center mb-3">
                <div className="text-xs text-gray-500 bg-gray-50 rounded-full px-3 py-1 inline-block">
                  {isNL ? 'Reageert doorgaans binnen 2 minuten' : 'Typically replies in 2 minutes'}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200">
              <div className="flex">
                <input
                  type="text"
                  placeholder={isNL ? 'Typ uw bericht...' : 'Type your message...'}
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && sendChatMessage()}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                />
                <button
                  onClick={sendChatMessage}
                  className="bg-primary text-white px-4 py-2 rounded-r hover:bg-primary/90 cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-send-plane-line"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}
        <button
          onClick={toggleLiveChat}
          className="bg-primary text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 cursor-pointer"
        >
          <div className="w-7 h-7 flex items-center justify-center">
            <i className="ri-chat-3-line ri-lg"></i>
          </div>
        </button>
      </div>

      <Footer />
    </div>
  );
}
